import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {Router, UrlTree} from '@angular/router';
import * as moment from 'moment';
import {ApiExamPlanInterface} from '@component/exam/exam.service';
import {AuthorizationService} from '@core/security/services/authorization.service';
import {RoleEnum} from '@core/security/enum/role.enum';
import {interval, Subscription} from 'rxjs';
import {ExamTypeEnum} from '@component/exam/card-exam/exam.type.enum';

export interface ExamEvent {
    type: ExamTypeEnum;
    data: ApiExamPlanInterface;
}

@Component({
    selector: 'app-card-exam[examPlan]',
    templateUrl: './card-exam.component.html',
    styleUrls: ['./card-exam.component.scss'],
})
export class AppCardExamComponent implements OnInit, OnDestroy {
    readonly #subscriptions: Subscription[] = [];

    readonly #router: Router;

    readonly #isTeacher: boolean;

    readonly roleEnum = RoleEnum;

    @Input()
    examPlan!: ApiExamPlanInterface;

    @Output()
    examEvent: EventEmitter<ExamEvent> = new EventEmitter();

    disabled: boolean = true;

    hasStarted: boolean = false;

    canStart: boolean = false;

    constructor(
        router: Router,
       private authorizationService: AuthorizationService,
    ) {
        this.#router = router;
        this.#isTeacher = this.authorizationService.isGranted(RoleEnum.RoleTeacher);
    }

    ngOnInit() {
        if (undefined === this.examPlan) {
            throw Error('Unable to load card, exam plan is undefined');
        }

        this.#update(); // Trigger update, after that, update every second
        this.#subscriptions.push(interval(1000).subscribe(() => this.#update()));
    }

    ngOnDestroy(): void {
        this.#subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
    }

    #update(): void {
        const canStart: boolean = this.canStart = moment().isSameOrAfter(this.examPlan.startDate) && moment().isSameOrBefore(this.examPlan.endDate);

        this.hasStarted = moment(this.examPlan.startDate).isBefore(moment());
        this.disabled = !this.#isTeacher && !canStart;
    }

    handelExamClick(): void {
        if (!this.canStart || this.#isTeacher) {
            return;
        }

        this.navigateToExam();
    }

    navigateToExam(newWindow: boolean = false): void {
        const examPlan: ApiExamPlanInterface = this.examPlan;
        const examPlanId: number | undefined = examPlan.id;

        if (undefined === examPlanId) {
            throw new Error('Cannot navigate to exam, exam plan ID is undefined');
        }

        const commands: string[] = ['books', examPlan.book.uuid, 'chapters', examPlan.chapter?.uuid, 'content-exams', examPlan.documentDpsId];

        if (!this.#isTeacher) {
            commands.push('plan', examPlanId.toString());
        }

        if (newWindow) {
            const urlTree: UrlTree = this.#router.createUrlTree(commands);
            const url = this.#router.serializeUrl(urlTree);

            window.open(url, '_blank');
        } else {
            this.#router.navigate(commands);
        }
    }

    editExam(): void {
        if (this.hasStarted) {
            return;
        }

        this.examEvent.emit({type:ExamTypeEnum.edit, data: this.examPlan});
    }

    deleteExam(): void {
        if (this.hasStarted) {
            return;
        }

        this.examEvent.emit({type: ExamTypeEnum.delete, data: this.examPlan});
    }
}
